import { createSlice } from "@reduxjs/toolkit";
import { getFeedInfo, getFeeds } from "./thunk";
import { getFeedFolderInfolderThunk, getFeedFolderRootThunk } from "../folder/thunk";

export const initialState = {
  feedsPage: 1,
  feedsLoading: false,
  feedsShowLoader: false,
  feedsHasMore: true,
  feedsFirstLoad: false,
  feeds: null,
  feedErr: false,
  feedErrMsg: null,
  query: null,
  selectedFeedId: null,
  sourceData: null,
  mappedSource: [],
  selectedPath: null,
  sourceDataFields: null,
  fields: null,
  postFeedBody: {
    name: "",
    type: "",
    interval: 86400 * 1000,
    canBeEmpty: false,
    feed: [],
    operations: [],
    source: [],
    brandId: "",
    maxAmount: 50,
    subOf: null
  },
  selectedOperationIdx: null,
  preview: [],
  previewErr: false,
  previewErrMsg: "",
  feedInfo: null,
  selectedFieldIdx: null,
  feedModalType: "feed",
  selectedFields: [],
  usingCampaigns: [],
  isChangesMade: false,
};

const feedSlice = createSlice({
  name: "Feed",
  initialState,
  reducers: {
    setFeedsPage(state, action) {
      state.feedsPage = action.payload;
    },
    setFeedsLoading(state, action) {
      state.feedsLoading = action.payload;
    },
    setFeedsShowLoader(state, action) {
      state.feedsShowLoader = action.payload;
    },
    setFeedsHasMore(state, action) {
      state.feedsHasMore = action.payload;
    },
    setFeedsFirstLoad(state, action) {
      state.feedsFirstLoad = action.payload;
    },
    getFeedSuccess(state, action) {
      state.feeds = action.payload;
    },
    setFeedErr(state, action) {
      state.feedErr = action.payload.feedErr;
      state.feedErrMsg = action.payload.feedErrMsg;
    },
    setQuery(state, action) {
      state.query = action.payload;
    },
    setSelectedFeedId(state, action) {
      state.selectedFeedId = action.payload;
    },
    getFeedInfoSuccess(state, action) {
      state.feedInfo = action.payload;
    },
    setSourceData(state, action) {
      state.sourceData = action.payload;
    },
    setMappedSource(state, action) {
      state.mappedSource = action.payload;
    },
    setSelectedPath(state, action) {
      state.selectedPath = action.payload;
    },
    setPostFeedBody(state, action) {
      state.postFeedBody = { ...state.postFeedBody, ...action.payload };
    },
    setSourceDataFields(state, action) {
      state.sourceDataFields = action.payload;
    },
    setFieldPathName(state, action) {
      state.fieldPathName = action.payload;
    },
    setFeedInfoFeed(state, action) {
      state.feedInfo.feed = action.payload;
    },
    setSelectedFieldIdx(state, action) {
      state.selectedFieldIdx = action.payload;
    },
    setSelectedFieldTargetUrl(state, action) {
      const foundField = state.feedInfo.feed.find((item, idx) => idx === state.selectedFieldIdx);
      foundField.targetUrl = action.payload;
    },
    setInputFieldTargetUrl(state, action) {
      const foundField = state.feedInfo.feed.find((item, idx) => idx === action.payload.idx);
      foundField.targetUrl = action.payload.targetUrl;
    },
    setSelectedFieldPath(state, action) {
      const foundField = state.feedInfo.feed[action.payload.idx];
      foundField.path = action.payload.path;
    },
    setSelectedFieldName(state, action) {
      const foundField = state.feedInfo.feed.find((item, idx) => idx === action.payload.idx);
      foundField.name = action.payload.name;
    },
    setFeedInfoOperations(state, action) {
      state.feedInfo.operations = action.payload;
    },
    setSelectedOperationIdx(state, action) {
      state.selectedOperationIdx = action.payload;
    },
    setOperation(state, action) {
      const foundOperation = state.feedInfo.operations.find((item, idx) => idx === action.payload.operationIdx);
      const keys = Object.keys(action.payload);
      keys.forEach(item => {
        if (item !== "operationIdx") {
          foundOperation[item] = action.payload[item];
        }
      });
    },
    setSelectedOperationPath(state, action) {
      const foundOperation = state.feedInfo.operations.find((item, idx) => idx === state.selectedOperationIdx);
      foundOperation.groupPath = action.payload.path.replace('.' + action.payload.name, '');
      foundOperation.relativePath = action.payload.name;
    },
    setSelectedOperationName(state, action) {
      const foundOperation = state.feedInfo.operations.find((item, idx) => idx === action.payload.selectedOperationIdx);
      foundOperation.name = action.payload.name;
    },
    addOperationMatches(state, action) {
      const { operationIdx, search, replace, disabled } = action.payload;
      state.feedInfo.operations[operationIdx].matches.push({ search, replace, disabled });
    },
    setOperationMatches(state, action) {
      const { operationIdx, matchIdx } = action.payload;
      const keys = Object.keys(action.payload);
      keys.forEach(item => {
        if (item !== "operationIdx" && item !== "matchIdx") {
          state.feedInfo.operations[operationIdx].matches[matchIdx][item] = action.payload[item];
        }
      })
    },
    removeOperationMatches(state, action) {
      const { operationIdx, matchIdx } = action.payload;
      state.feedInfo.operations[operationIdx].matches.splice(Number(matchIdx), 1);
      // const foundOperation = state.feedInfo.operations.find((item, idx) => idx === action.payload.operationIdx);
      // foundOperation.matches.pop();
    },
    toggleOperationCaseSensitive(state, action) {
      const { operationIdx } = action.payload;
      state.feedInfo.operations[operationIdx].caseSensitive = !state.feedInfo.operations[operationIdx].caseSensitive;
    },
    setPreview(state, action) {
      state.preview = action.payload;
    },
    setPreviewErr(state, action) {
      state.previewErr = action.payload;
    },
    setPreviewErrMsg(state, action) {
      state.previewErrMsg = action.payload;
    },
    setSelectedFields(state, action) {
      state.selectedFields = action.payload;
    },
    addField(state) {
      let addedFields = state.selectedFields.map((item) => {
        return {
          "targetUrl": "",
          "name": item.name,
          "path": item.path
        };
      });
      state.feedInfo.feed = [...state.feedInfo.feed, ...addedFields];
      state.selectedFields = [];
      state.selectedPath = null;
    },
    setUsingCampaigns(state, action) {
      state.usingCampaigns = action.payload;
    },
    setIsChangesMade(state, action) {
      state.isChangesMade = action.payload;
    },
    resetFeedState(state) {
      state.feeds = null;
      state.feedErr = false;
      state.feedErrMsg = null;
      state.query = null;
      state.selectedFeedId = null;
      state.sourceData = null;
      state.mappedSource = [];
      state.selectedPath = null;
      state.sourceDataFields = null;
      state.postFeedBody = {
        name: "",
        type: "",
        interval: 86400 * 1000,
        canBeEmpty: false,
        feed: [],
        operations: [],
        source: [],
        brandId: "",
        maxAmount: 50,
        subOf: null
      };
      state.selectedOperationIdx = null;
      state.preview = [];
      state.previewErr = false;
      state.previewErrMsg = "";
      state.feedInfo = null;
      state.selectedFieldIdx = null;
      state.feedModalType = "feed";
      state.selectedFields = [];
      state.isChangesMade = false;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getFeeds.pending, (state, action) => {
      state.feedsShowLoader = true;
    });
    builder.addCase(getFeeds.fulfilled, (state, action) => {
      state.feeds = action.payload;
      state.feedsShowLoader = false;
    });
    builder.addCase(getFeedInfo.fulfilled, (state, action) => {
      state.feedInfo = action.payload;
    });
  }
});

export const {
  setFeedsPage,
  setFeedsLoading,
  setFeedsShowLoader,
  setFeedsHasMore,
  setFeedsFirstLoad,
  getFeedSuccess,
  setFeedErr,
  setQuery,
  setSelectedFeedId,
  setSourceData,
  setMappedSource,
  setSelectedPath,
  setPostFeedBody,
  setSourceDataFields,
  setFieldPathName,
  getFeedInfoSuccess,
  setFeedInfoFeed,
  setFeedInfoOperations,
  setSelectedFieldIdx,
  setSelectedFieldTargetUrl,
  setInputFieldTargetUrl,
  setSelectedFieldPath,
  setSelectedFieldName,
  setSelectedOperationIdx,
  setOperation,
  setSelectedOperationPath,
  setSelectedOperationName,
  addOperationMatches,
  setOperationMatches,
  removeOperationMatches,
  toggleOperationCaseSensitive,
  setPreview,
  setPreviewErr,
  setPreviewErrMsg,
  setIsAddingNewFeed,
  setSelectedFields,
  addField,
  setUsingCampaigns,
  setIsChangesMade,
  resetFeedState
} = feedSlice.actions;

export default feedSlice.reducer